import React, { useContext, useEffect, useRef, useState } from 'react';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { Functionality } from '../context/Functionality';
import Article, { safeLink } from './blog/Article';
import './blog/blog.css';

const fields = ['title', 'subtitle', 'author', 'date', 'content', 'slug', 'img1', 'img1Alt', 'img2', 'img2Alt', 'img3', 'img3Alt', 'metaTitle', 'metaDescription'];
const blank = Object.fromEntries(fields.map(field => [field, '']));
const inputFor = post => Object.fromEntries(fields.map(field => [field, field === 'date' ? (post.date || '').slice(0, 10) : post[field] || '']));
const slugFrom = value => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'untitled';
const errorText = error => error.response?.data?.errMsg || 'Unable to save your changes. Your writing is still here. Please try again.';

export default function AddBlog() {
    const { blogId } = useParams();
    const { getSingleBlogDetail, saveBlog, deleteBlog } = useContext(Functionality);
    const api = useRef({ getSingleBlogDetail, saveBlog, deleteBlog });
    api.current = { getSingleBlogDetail, saveBlog, deleteBlog };
    const navigate = useNavigate();
    const [form, setForm] = useState(blank);
    const [baseline, setBaseline] = useState(JSON.stringify(blank));
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(Boolean(blogId));
    const [loadError, setLoadError] = useState('');
    const [loadAttempt, setLoadAttempt] = useState(0);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [pending, setPending] = useState(false);
    const busy = useRef(false);
    const allowNavigation = useRef(false);
    const [preview, setPreview] = useState(false);
    const contentRef = useRef(null);
    const dirty = JSON.stringify(form) !== baseline;
    const blocker = useBlocker(() => !allowNavigation.current && (dirty || busy.current));
    useEffect(() => {
        if (blocker.state === 'blocked') {
            if (busy.current) { blocker.reset(); return; }
            if (window.confirm('You have unsaved changes. Leave without saving?')) blocker.proceed();
            else blocker.reset();
        }
    }, [blocker]);
    useEffect(() => {
        const warn = event => { if (dirty || busy.current) { event.preventDefault(); event.returnValue = ''; } };
        const beforeLogout = event => {
            if (busy.current || (dirty && !window.confirm('You have unsaved changes. Sign out without saving?'))) event.preventDefault();
        };
        window.addEventListener('beforeunload', warn);
        window.addEventListener('blog-before-logout', beforeLogout);
        return () => {
            window.removeEventListener('beforeunload', warn);
            window.removeEventListener('blog-before-logout', beforeLogout);
        };
    }, [dirty]);
    useEffect(() => {
        let active = true;
        if (!blogId) return;
        setLoading(true); setLoadError('');
        api.current.getSingleBlogDetail(blogId).then(data => {
            if (!active) return;
            setPost(data); setForm(inputFor(data)); setBaseline(JSON.stringify(inputFor(data)));
        }).catch(err => { if (active) setLoadError(err.response?.data?.errMsg || 'Unable to load this article. Please try again.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [blogId, loadAttempt]);

    function change(event) { const { name, value } = event.target; setForm(current => ({ ...current, [name]: value })); setMessage(''); }
    async function save(status) {
        if (busy.current) return;
        busy.current = true; setPending(true); setError(''); setMessage('');
        try {
            const saved = await api.current.saveBlog(post?._id || blogId, { ...form, status });
            setPost(saved); setForm(inputFor(saved)); setBaseline(JSON.stringify(inputFor(saved)));
            setMessage(status === 'published' ? 'Article published and changes saved.' : 'Draft saved. This article is private.');
        } catch (err) { setError(errorText(err)); }
        finally { busy.current = false; setPending(false); }
    }
    async function remove() {
        if (busy.current || !window.confirm('Delete this article permanently? This cannot be undone.')) return;
        busy.current = true; setPending(true); setError('');
        try {
            await api.current.deleteBlog(post._id);
            allowNavigation.current = true;
            navigate('/blog-post', { state: { message: 'Article deleted.' } });
        } catch (err) { setError(err.response?.data?.errMsg || 'Unable to delete this article. Please try again.'); }
        finally { busy.current = false; setPending(false); }
    }
    function format(kind) {
        const area = contentRef.current;
        const start = area.selectionStart, end = area.selectionEnd;
        const selected = form.content.slice(start, end);
        let text;
        if (kind === 'Heading') text = `\n\n## ${selected || 'Heading'}\n\n`;
        if (kind === 'Bold') text = `**${selected || 'bold text'}**`;
        if (kind === 'List') text = `\n${(selected || 'List item').split('\n').map(line => `- ${line}`).join('\n')}\n`;
        if (kind === 'Link') {
            const url = window.prompt('Enter a complete https:// link:');
            if (url === null) return;
            if (!safeLink(url)) { setError('Use an HTTP, HTTPS, email, or same-site link.'); return; }
            text = `[${selected || 'link text'}](${url.replace(/\(/g, '%28').replace(/\)/g, '%29')})`;
        }
        setForm(current => ({ ...current, content: current.content.slice(0, start) + text + current.content.slice(end) }));
        area.focus();
    }
    if (loading) return <p role="status">Loading article…</p>;
    if (loadError) return <div><p role="alert">{loadError}</p><button onClick={() => setLoadAttempt(value => value + 1)}>Retry loading</button></div>;
    const published = post?.status === 'published';
    return <div className="blog-editor">
        <h1 className="text-3xl font-bold">{post ? 'Edit article' : 'New article'}</h1>
        <p>{published ? 'Published' : 'Draft'}{dirty ? ' · Unsaved changes' : ''}</p>
        {error && <p role="alert" className="blog-message blog-error">{error}</p>}
        {message && <p role="status" className="blog-message">{message}</p>}
        <div className="blog-actions"><button onClick={() => navigate('/blog-post')}>Back to articles</button><button onClick={() => setPreview(value => !value)}>{preview ? 'Back to editor' : 'Preview'}</button></div>
        {preview ? <><p className="blog-message">Private preview · Includes your unsaved changes</p><Article post={{ ...post, ...form }} /></> :
            <form onSubmit={event => { event.preventDefault(); save(published ? 'published' : 'draft'); }}>
                <fieldset disabled={pending}>
                    {['title', 'subtitle', 'author'].map(name => <React.Fragment key={name}><label htmlFor={name}>{name[0].toUpperCase() + name.slice(1)}</label><input id={name} name={name} value={form[name]} onChange={change} maxLength={name === 'subtitle' ? 500 : 200} /></React.Fragment>)}
                    <label htmlFor="slug">Article address</label><input id="slug" name="slug" value={form.slug} onChange={change} placeholder={slugFrom(form.title)} disabled={Boolean(post?.firstPublishedAt)} maxLength={160} />
                    <p>/blog/{form.slug || slugFrom(form.title)} · Address is fixed after first publication.</p>
                    <label htmlFor="date">Original article date (optional)</label><input id="date" type="date" name="date" value={form.date} onChange={change} />
                    <label htmlFor="content">Article content</label>
                    <div className="blog-toolbar" aria-label="Formatting">{['Heading', 'Bold', 'Link', 'List'].map(kind => <button key={kind} type="button" onClick={() => format(kind)}>{kind}</button>)}</div>
                    <textarea ref={contentRef} id="content" name="content" className="blog-body" value={form.content} onChange={change} maxLength={100000} />
                    <p>Supports Markdown. HTML is shown as text. Preview to check formatting.</p>
                    <p>Image 1 leads the article. Place [[image:2]] or [[image:3]] on its own line between sections to position supporting images, or let the layout place them automatically.</p>
                    {[1, 2, 3].map(number => <React.Fragment key={number}>
                        <label htmlFor={`img${number}`}>Image {number} URL (optional)</label><input type="url" id={`img${number}`} name={`img${number}`} value={form[`img${number}`]} onChange={change} />
                        <label htmlFor={`img${number}Alt`}>Image {number} description</label><input id={`img${number}Alt`} name={`img${number}Alt`} value={form[`img${number}Alt`]} onChange={change} />
                    </React.Fragment>)}
                    <label htmlFor="metaTitle">Search title (optional)</label><input id="metaTitle" name="metaTitle" value={form.metaTitle} onChange={change} maxLength={200} />
                    <label htmlFor="metaDescription">Search description (optional)</label><textarea id="metaDescription" name="metaDescription" value={form.metaDescription} onChange={change} maxLength={1000} />
                    <div className="blog-actions"><button className="primary" type="submit">{published ? 'Save Changes' : 'Save Draft'}</button></div>
                </fieldset>
            </form>}
        <div className="blog-actions">
            {!published && <button className="primary" disabled={pending} onClick={() => save('published')}>Publish</button>}
            {published && <button disabled={pending} onClick={() => save('draft')}>Unpublish</button>}
            {post && <button className="danger" disabled={pending} onClick={remove}>Delete article</button>}
        </div>
        {pending && <p role="status">Saving…</p>}
    </div>;
}
